import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import LinearProgress from '@mui/material/LinearProgress';

import { useData } from '../../../contexts/DataContext';
import { formatPoints } from '../../../utils/leaderboardUtils';
import { soft, softHeadCellSx, softBodyCellSx } from './softStyles';

// One player's season, week by week, opened from a standings row. Reads that
// player's weeklyPoints docs (one query). Weeks with no doc show "—": either
// not published yet or the player made no picks that week. Clicking a week
// closes the dialog and opens that week's picks below the standings.

const headSx = { ...softHeadCellSx, padding: '8px 12px' };
const cellSx = { ...softBodyCellSx, padding: '10px 12px', whiteSpace: 'nowrap' };

const WeeklyBreakdownModal = ({ open, onClose, season, player, currentWeek, onSelectWeek }) => {
  const { getUserWeeklyPoints } = useData();
  // Tagged with whose weeks they are, so reopening for another player shows
  // the progress bar instead of the previous player's rows.
  const [loaded, setLoaded] = useState({ email: null, weeks: [] });
  // A clicked week waits for the close transition to finish: while the
  // dialog is open the page can't scroll, and restoring focus to the
  // standings row would scroll back up to it.
  const [chosenWeek, setChosenWeek] = useState(null);

  useEffect(() => {
    if (!open || !player) return;
    let cancelled = false;
    getUserWeeklyPoints(season, player.email).then((weeks) => {
      if (!cancelled) setLoaded({ email: player.email, weeks });
    });
    return () => { cancelled = true; };
  }, [open, player, season, getUserWeeklyPoints]);

  const isLoading = !!player && loaded.email !== player.email;
  const weeks = isLoading ? [] : loaded.weeks;

  // Weeks 1..currentWeek, plus any published week past it (an admin may have
  // moved currentWeek back). Only weeks the week selector offers are clickable.
  const byWeek = new Map(weeks.map((w) => [w.week, w]));
  const lastWeek = Math.max(currentWeek || 0, ...weeks.map((w) => w.week), 0);
  let running = 0;
  const rows = Array.from({ length: lastWeek }, (_, i) => {
    const week = i + 1;
    const data = byWeek.get(week);
    if (data) running += Number(data.total) || 0;
    return { week, data, running, canOpen: week <= (currentWeek || 0) };
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="weekly-breakdown-title"
      disableRestoreFocus={chosenWeek !== null}
      slotProps={{
        paper: { sx: { borderRadius: '16px' } },
        transition: {
          onExited: () => {
            if (chosenWeek !== null) onSelectWeek?.(chosenWeek);
            setChosenWeek(null);
          },
        },
      }}
    >
      <DialogTitle id="weekly-breakdown-title" sx={{ pr: 6 }}>
        {player?.displayName}
        {player && (
          <div style={{ fontSize: '13px', fontWeight: 400, color: soft.muted }}>
            {formatPoints(player.totalPoints)} pts · {player.rankLabel} place
          </div>
        )}
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1, sm: 3 } }}>
        {isLoading && <LinearProgress />}
        {!isLoading && (
          <Table
            size="small"
            aria-label="weekly points"
            sx={{ borderCollapse: 'separate', borderSpacing: 0, borderRadius: soft.radius, overflow: 'hidden', boxShadow: `0 0 0 1px ${soft.outline}` }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>Week</TableCell>
                <TableCell sx={headSx} align="right">Winner</TableCell>
                <TableCell sx={headSx} align="right">Score</TableCell>
                <TableCell sx={headSx} align="right">Week</TableCell>
                <TableCell sx={headSx} align="right">Running</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ week, data, running, canOpen }) => (
                <TableRow
                  key={week}
                  hover={canOpen}
                  onClick={canOpen ? () => { setChosenWeek(week); onClose(); } : undefined}
                  sx={canOpen ? { cursor: 'pointer' } : undefined}
                  title={canOpen ? `Show week ${week} picks` : undefined}
                >
                  <TableCell sx={cellSx}>{week}</TableCell>
                  <TableCell sx={cellSx} align="right">{data ? formatPoints(data.pickWinnerPoints || 0) : '—'}</TableCell>
                  <TableCell sx={cellSx} align="right">{data ? formatPoints(data.pickScorePoints || 0) : '—'}</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 500 }} align="right">{data ? formatPoints(data.total || 0) : '—'}</TableCell>
                  <TableCell sx={{ ...cellSx, color: soft.muted }} align="right">{formatPoints(running)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div style={{ fontSize: '12px', color: soft.faint, marginTop: '10px' }}>
          Click a week to see its picks. "—" means no published score that week.
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WeeklyBreakdownModal;
